'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Scrollbar, A11y, Navigation } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
// import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { PortableText } from '@portabletext/react';
import { components } from './PortableTextComponents';
import { PortableTextBlock } from 'sanity';
import { m, useInView } from 'framer-motion';
import { TestimonialSlide } from '../shared/carousel';
import { fadeUp, moveUp, fadeUpRight } from '@/assets/framer/animations';
import { sectionPadding } from '../utils/utils';

type Slide = {
  type: 'image' | 'quote' | 'video';
  articleImage?: boolean;
  image?: {
    url: string;
    alt: string;
    mediaAlt: string;
    blurDataUrl: string;
  };
  video?: {
    video: {
      id: string;
    };
    previewTiming: {
      start: number;
      end: number;
    };
  };
  text?: PortableTextBlock;
  quote?: PortableTextBlock;
  quoteImage?: {
    url: string;
    alt: string;
    mediaAlt?: string;
    blurDataUrl: string;
  };
  article?: {
    slug: string;
    image: {
      url: string;
      alt: string;
      blurDataUrl: string;
      mediaAlt: string;
    };
  };
};

export default function TestimonialCarousel(props: {
  data?: {
    sectionId: string;
    heading?: PortableTextBlock;
    subheading?: PortableTextBlock;
    slides?: Slide[];
    displayOptions?: {
      padding: 'py' | 'pb' | 'pt';
    };
  };
}) {
  const [, setSwiper] = useState<SwiperClass | null>(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.5, once: true });
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const subheading = props.data?.subheading;
  const slides = props.data?.slides ?? [];
  const displayOptions = props.data?.displayOptions;

  const sectionPaddingClass = sectionPadding(displayOptions?.padding);

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const slideClass = `rounded-lg overflow-hidden bg-transparent !overflow-visible !h-[unset] flex-grow !w-[unset] asepect-square 2xl:max-w-[450px] lg:max-w-[420px] sm:max-w-[350px] max-w-[315px]`;
  const paginationButtonClass = `disabled:opacity-0 cursor-pointer [&_path]:fill-black fill-black h-12 w-12 shadow-lg absolute rounded-full bg-yellow hover:bg-yellow-600 flex items-center justify-center hover:scale-105 duration-100 transform z-20 p-2.5`;
  const leftButton = `${paginationButtonClass} left-0`;
  const rightButton = `${paginationButtonClass} right-0`;

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (
        (event.key === 'Escape' && activeVideo) ||
        (event.key === 'Esc' && activeVideo)
      ) {
        setActiveVideo(null);
      }
    };

    window.addEventListener('keydown', handleEscKeyPress);

    return () => {
      window.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [activeVideo]);

  const [activeSlide, setActiveSlide] = useState<number>(0);

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveSlide(swiper.activeIndex);
  };

  return (
    <>
      <section
        id={sectionId}
        ref={sectionRef}
        className={`${sectionPaddingClass} from-white-200 relative scroll-m-16 bg-gradient-to-b from-white via-yellow-50 to-white`}
      >
        <div className='overflow-hidden'>
          <div className='container-padding container relative'>
            <div className='relative z-10 space-y-4'>
              {heading && (
                <m.h2
                  initial='hidden'
                  animate={isInView ? 'visible' : 'hidden'}
                  variants={fadeUp}
                  transition={{ delay: 0.1 }}
                  className='heading-xl font-display font-medium text-charcoal [&_strong]:text-pink'
                >
                  <PortableText
                    value={heading}
                    components={components}
                  ></PortableText>
                </m.h2>
              )}
              {subheading && (
                <m.h3
                  initial='hidden'
                  animate={isInView ? 'visible' : 'hidden'}
                  variants={fadeUp}
                  transition={{ delay: 0.2 }}
                  className='heading-xs max-w-[80%] text-charcoal [&_strong]:text-pink'
                >
                  <PortableText
                    value={subheading}
                    components={components}
                  ></PortableText>
                </m.h3>
              )}
            </div>

            <m.div
              className='relative z-10'
              initial='outOfView'
              animate={isInView ? 'visible' : 'outOfView'}
              variants={moveUp}
            >
              <Swiper
                className='!overflow-visible [&>.swiper-wrapper]:my-10 [&>.swiper-wrapper]:space-x-5 [&>.swiper-wrapper]:overflow-visible lg:[&>.swiper-wrapper]:space-x-8 sm:[&_.swiper-pagination]:!bottom-8'
                modules={[Navigation, Scrollbar, A11y]}
                onInit={(swiper: SwiperClass) => {
                  setSwiper(swiper);
                }}
                onSlideChange={handleSlideChange}
                breakpoints={{
                  0: {
                    slidesPerView: 'auto',
                    spaceBetween: 0,
                  },
                  // when window width is >= 425px
                  425: {
                    slidesPerView: 'auto',
                    spaceBetween: 0,
                  },
                  // when window width is >= 765px
                  765: {
                    slidesPerView: 'auto',
                    spaceBetween: 0,
                  },
                  // when window width is >= 980px
                  980: {
                    slidesPerView: 'auto',
                    spaceBetween: 0,
                  },
                }}
                navigation={{
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                  disabledClass: 'disabled',
                }}
              >
                {slides.length > 0 &&
                  slides.map((slide, index) => (
                    <SwiperSlide
                      className={slideClass}
                      key={`home-carousel-${index}`}
                    >
                      <TestimonialSlide
                        slide={slide}
                        key={index}
                        activeSlide={activeSlide}
                        index={index}
                      />
                    </SwiperSlide>
                  ))}
                <div className='absolute left-0 top-[50%] z-40 block w-full -translate-y-8 transform'>
                  <button ref={prevRef} className={leftButton}>
                    <svg
                      className='h-5 w-5 rotate-180 transform'
                      xmlns='http://www.w3.org/2000/svg'
                      width='17'
                      height='16'
                      viewBox='0 0 17 16'
                      fill='none'
                    >
                      <path
                        d='M9.81223 15.4466L16.2143 9.0506C16.3186 8.95578 16.4019 8.84051 16.4589 8.7121C16.5159 8.58369 16.5453 8.44494 16.5453 8.30466C16.5453 8.16438 16.5159 8.02564 16.4589 7.89723C16.4019 7.76881 16.3186 7.65354 16.2143 7.55872L9.81223 1.16268C9.62179 0.977924 9.37156 0.865835 9.10577 0.84623C8.83997 0.826625 8.57574 0.900765 8.35979 1.05554C8.23002 1.14171 8.12192 1.25621 8.04381 1.39025C7.96569 1.52429 7.91964 1.67429 7.9092 1.82873C7.89876 1.98316 7.9242 2.13791 7.98357 2.28108C8.04294 2.42424 8.13466 2.55201 8.25167 2.65456L12.8785 7.23732L1.47524 7.23732C1.19718 7.248 0.934093 7.36495 0.741174 7.56365C0.548254 7.76235 0.440491 8.02735 0.440491 8.30306C0.440491 8.57878 0.548254 8.84378 0.741174 9.04248C0.934093 9.24118 1.19718 9.35813 1.47524 9.3688L12.8801 9.3688L8.25328 13.9532C8.13627 14.0557 8.04455 14.1835 7.98518 14.3266C7.92581 14.4698 7.90037 14.6246 7.91081 14.779C7.92126 14.9334 7.96731 15.0834 8.04542 15.2175C8.12354 15.3515 8.23164 15.466 8.36141 15.5522C8.57736 15.707 8.84159 15.7811 9.10738 15.7615C9.37317 15.7419 9.62341 15.6298 9.81385 15.445L9.81223 15.4466Z'
                        fill='none'
                      />
                    </svg>
                  </button>
                  <button ref={nextRef} className={rightButton}>
                    <svg
                      className='h-5 w-5'
                      xmlns='http://www.w3.org/2000/svg'
                      width='17'
                      height='16'
                      viewBox='0 0 17 16'
                      fill='none'
                    >
                      <path
                        d='M9.81223 15.4466L16.2143 9.0506C16.3186 8.95578 16.4019 8.84051 16.4589 8.7121C16.5159 8.58369 16.5453 8.44494 16.5453 8.30466C16.5453 8.16438 16.5159 8.02564 16.4589 7.89723C16.4019 7.76881 16.3186 7.65354 16.2143 7.55872L9.81223 1.16268C9.62179 0.977924 9.37156 0.865835 9.10577 0.84623C8.83997 0.826625 8.57574 0.900765 8.35979 1.05554C8.23002 1.14171 8.12192 1.25621 8.04381 1.39025C7.96569 1.52429 7.91964 1.67429 7.9092 1.82873C7.89876 1.98316 7.9242 2.13791 7.98357 2.28108C8.04294 2.42424 8.13466 2.55201 8.25167 2.65456L12.8785 7.23732L1.47524 7.23732C1.19718 7.248 0.934093 7.36495 0.741174 7.56365C0.548254 7.76235 0.440491 8.02735 0.440491 8.30306C0.440491 8.57878 0.548254 8.84378 0.741174 9.04248C0.934093 9.24118 1.19718 9.35813 1.47524 9.3688L12.8801 9.3688L8.25328 13.9532C8.13627 14.0557 8.04455 14.1835 7.98518 14.3266C7.92581 14.4698 7.90037 14.6246 7.91081 14.779C7.92126 14.9334 7.96731 15.0834 8.04542 15.2175C8.12354 15.3515 8.23164 15.466 8.36141 15.5522C8.57736 15.707 8.84159 15.7811 9.10738 15.7615C9.37317 15.7419 9.62341 15.6298 9.81385 15.445L9.81223 15.4466Z'
                        fill='none'
                      />
                    </svg>
                  </button>
                </div>
              </Swiper>
            </m.div>

            {/* Background SVG */}
            <m.svg
              className='absolute right-0 top-0 z-0 hidden lg:block [&_path]:fill-pink'
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUpRight}
              transition={{ delay: 0.3 }}
              xmlns='http://www.w3.org/2000/svg'
              width='234'
              height='203'
              viewBox='0 0 234 203'
              fill='none'
            >
              <path
                d='M64.0621 133.367L52.077 133.344L52.0992 121.952L64.0843 121.976L64.0621 133.367ZM77.137 121.095L65.5943 121.073L65.6177 109.057L77.1604 109.079L77.137 121.095ZM90.5362 107.495L78.4505 107.472L78.4743 95.2748L90.56 95.2984L90.5362 107.495ZM103.995 94.4993L91.6475 94.4753L91.6733 81.2518L104.02 81.2759L103.995 94.4993ZM116.992 80.4558L104.907 80.4323L104.932 67.5711L117.017 67.5947L116.992 80.4558ZM130.68 94.2696L118.594 94.246L118.62 81.3043L130.705 81.3279L130.68 94.2696ZM144.188 107.318L132.102 107.295L132.126 95.0976L144.212 95.1211L144.188 107.318ZM157.474 120.688L145.389 120.665L145.412 108.911L157.497 108.934L157.474 120.688ZM145.722 135.298L157.808 135.321L157.786 146.713L145.7 146.69L145.722 135.298ZM132.385 148.274L144.47 148.297L144.447 160.494L132.361 160.471L132.385 148.274ZM105.084 175.05L117.17 175.074L117.145 187.935L105.059 187.911L105.084 175.05ZM91.8799 160.955L104.227 160.979L104.201 174.203L91.8541 174.179L91.8799 160.955ZM78.7337 147.908L90.8193 147.931L90.7956 160.128L78.7099 160.105L78.7337 147.908ZM90.144 133.297L78.6818 133.275L78.7036 122.085L90.1658 122.107L90.144 133.297ZM103.823 120.845L91.7369 120.822L91.7598 109.068L103.845 109.091L103.823 120.845ZM130.789 120.898L118.442 120.874L118.465 108.858L130.812 108.882L130.789 120.898ZM118.754 146.637L118.775 135.507L130.901 135.53C130.901 135.53 131.042 135.47 131.123 135.43L131.101 146.661L118.754 146.637ZM116.829 133.349L104.743 133.326L104.765 122.135L116.851 122.159L116.829 133.349ZM92.0704 135.455L104.156 135.478L104.135 146.347L92.0492 146.323L92.0704 135.455ZM105.419 147.96L117.766 147.984L117.741 160.442L105.394 160.418L105.419 147.96ZM143.795 133.664L131.669 133.64C131.669 133.64 131.528 133.7 131.448 133.74L131.471 121.926L143.818 121.95L143.795 133.684L143.795 133.664ZM117.483 107.528L105.136 107.504L105.16 95.045L117.507 95.0691L117.483 107.528ZM65.9077 135.686L77.4504 135.708L77.4297 146.315L65.887 146.293L65.9077 135.686ZM130.886 174.255L118.8 174.231L118.826 161.29L130.911 161.313L130.886 174.255ZM170.48 133.736L158.394 133.712L158.417 121.978L170.503 122.002L170.48 133.736ZM37.984 121.18C37.6824 121.179 37.4406 121.42 37.44 121.722L37.4136 135.268C37.413 135.57 37.6539 135.812 37.9555 135.812L51.2678 135.838L51.2453 147.411C51.2447 147.713 51.4855 147.955 51.7871 147.956L64.0739 147.98L64.0498 160.358C64.0492 160.66 64.29 160.902 64.5916 160.902L77.2202 160.927L77.1944 174.17C77.1938 174.472 77.4347 174.714 77.7363 174.715L90.4253 174.74L90.399 188.245C90.3984 188.547 90.6392 188.789 90.9408 188.789L103.992 188.815L103.966 201.978C103.965 202.28 104.206 202.522 104.508 202.522L118.041 202.549C118.343 202.549 118.585 202.308 118.586 202.006L118.611 188.843L131.179 188.868C131.481 188.868 131.723 188.627 131.723 188.325L131.749 175.082L144.921 175.107C145.222 175.108 145.464 174.867 145.465 174.565L145.491 161.322L158.481 161.347C158.783 161.347 159.025 161.106 159.025 160.804L159.049 148.326L171.819 148.351C172.12 148.351 172.362 148.11 172.363 147.808L172.387 135.35L184.513 135.373C184.815 135.374 185.056 135.133 185.057 134.831L185.083 121.285C185.084 120.984 184.843 120.742 184.542 120.741L172.054 120.717L172.079 107.916C172.079 107.614 171.838 107.372 171.537 107.371L158.767 107.346L158.792 94.8678C158.792 94.5659 158.551 94.3239 158.25 94.3233L145.259 94.298L145.285 81.0544C145.286 80.7525 145.045 80.5105 144.743 80.5099L131.572 80.4842L131.597 67.2608C131.598 66.9589 131.357 66.7169 131.055 66.7163L118.487 66.6918L118.513 53.5288C118.513 53.2269 118.273 52.9849 117.971 52.9843L104.437 52.9579C104.136 52.9573 103.894 53.1984 103.893 53.5003L103.868 66.6634L90.8169 66.6379C90.5153 66.6373 90.2735 66.8784 90.2729 67.1803L90.2466 80.6855L77.5576 80.6608C77.256 80.6602 77.0142 80.9012 77.0136 81.2031L76.9878 94.4467L64.3592 94.4221C64.0576 94.4215 63.8158 94.6626 63.8152 94.9645L63.7911 107.343L51.5044 107.319C51.2027 107.318 50.961 107.559 50.9604 107.861L50.9344 121.205L37.984 121.18Z'
                fill='none'
              />
              <path
                d='M191.984 28.1507C192.285 28.1513 192.527 27.9103 192.528 27.6084L192.553 14.8277L205.764 14.8535L205.739 27.6341C205.739 27.936 205.98 28.178 206.281 28.1786L218.588 28.2026L218.563 41.3656L205.793 41.3407C205.492 41.3402 205.25 41.5812 205.249 41.8831L205.224 54.9858L192.796 54.9616L192.822 41.9193C192.822 41.6174 192.581 41.3754 192.28 41.3748L179.168 41.3492L179.194 28.1258L191.964 28.1507L191.984 28.1507ZM164.99 42.1066L178.101 42.1321L178.075 55.5769C178.074 55.8788 178.315 56.1208 178.617 56.1214L191.949 56.1474L191.924 69.1897C191.923 69.4916 192.164 69.7336 192.466 69.7342L206.14 69.7608C206.442 69.7614 206.683 69.5204 206.684 69.2185L206.71 56.0957L219.479 56.1205C219.781 56.1211 220.022 55.8801 220.023 55.5782L220.049 42.1333L232.818 42.1582C233.12 42.1588 233.362 41.9178 233.362 41.6159L233.389 27.9496C233.39 27.6477 233.149 27.4057 232.847 27.4051L220.54 27.3812L220.566 13.9967C220.567 13.6948 220.326 13.4528 220.025 13.4522L206.592 13.4261L206.617 0.625289C206.617 0.323384 206.376 0.0813902 206.075 0.0808024L192.42 0.0541948C192.119 0.053607 191.877 0.294661 191.876 0.596566L191.852 13.3571L178.419 13.3309C178.117 13.3303 177.875 13.5714 177.875 13.8733L177.848 27.3181L165.099 27.2933C164.798 27.2927 164.556 27.5337 164.555 27.8356L164.529 41.5019C164.528 41.8038 164.769 42.0457 165.07 42.0463'
                fill='none'
              />
              <path
                d='M28.4199 44.6382L15.6103 44.6132L15.6337 32.5974L28.4433 32.6223L28.4199 44.6382ZM1.38942 46.4573L14.7017 46.4832L14.6776 58.8412C14.6771 59.1431 14.9179 59.3851 15.2196 59.3857L28.8938 59.4123C29.1955 59.4129 29.4373 59.1719 29.4379 58.87L29.4639 45.5258L42.6354 45.5514C42.937 45.552 43.1788 45.311 43.1794 45.0091L43.2061 31.3227C43.2066 31.0208 42.9658 30.7788 42.6642 30.7782L29.3519 30.7523L29.376 18.3943C29.3766 18.0924 29.1357 17.8504 28.834 17.8498L15.1598 17.8232C14.8581 17.8226 14.6164 18.0637 14.6158 18.3656L14.5898 31.7098L1.41821 31.6841C1.11657 31.6835 0.874811 31.9246 0.874222 32.2265L0.847591 45.8927C0.847003 46.1946 1.08782 46.4366 1.38946 46.4372'
                fill='none'
              />
            </m.svg>

            {/* Background SVG Mobile */}
            <m.svg
              className='absolute -right-20 top-0 z-0 h-[150px] w-[150px] lg:hidden [&_path]:fill-pink'
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUpRight}
              transition={{ delay: 0.3 }}
              xmlns='http://www.w3.org/2000/svg'
              width='205'
              height='151'
              viewBox='0 0 205 151'
              fill='none'
            >
              <path
                d='M83.027 81.3928L71.0419 81.3694L71.0641 69.9775L83.0492 70.0009L83.027 81.3928ZM96.1019 69.1206L84.5591 69.0981L84.5825 57.0823L96.1253 57.1048L96.1019 69.1206ZM109.501 55.5207L97.4154 55.4972L97.4391 43.3002L109.525 43.3237L109.501 55.5207ZM122.959 42.5247L110.612 42.5007L110.638 29.2772L122.985 29.3013L122.959 42.5247ZM135.957 28.4812L123.872 28.4577L123.897 15.5965L135.982 15.6201L135.957 28.4812ZM149.645 42.2949L137.559 42.2714L137.584 29.3297L149.67 29.3533L149.645 42.2949ZM163.153 55.3435L151.067 55.3199L151.091 43.123L163.177 43.1465L163.153 55.3435ZM176.439 68.7136L164.354 68.6901L164.377 56.9359L176.462 56.9595L176.439 68.7136ZM164.687 83.3231L176.773 83.3466L176.751 94.7385L164.665 94.715L164.687 83.3231ZM151.35 96.2992L163.435 96.3227L163.411 108.52L151.326 108.496L151.35 96.2992ZM124.049 123.075L136.135 123.099L136.11 135.96L124.024 135.936L124.049 123.075ZM110.845 108.981L123.192 109.005L123.166 122.228L110.819 122.204L110.845 108.981ZM97.6985 95.933L109.784 95.9565L109.76 108.153L97.6748 108.13L97.6985 95.933ZM109.109 81.3228L97.6466 81.3005L97.6685 70.1099L109.131 70.1322L109.109 81.3228ZM122.787 68.8707L110.702 68.8472L110.725 57.093L122.81 57.1165L122.787 68.8707ZM149.754 68.9233L137.407 68.8992L137.43 56.8834L149.777 56.9075L149.754 68.9233ZM137.719 94.6625L137.74 83.5322L149.866 83.5559C149.866 83.5559 150.007 83.4957 150.088 83.4556L150.066 94.6865L137.719 94.6625ZM135.794 81.3748L123.708 81.3513L123.73 70.1607L135.816 70.1842L135.794 81.3748ZM111.035 83.4802L123.121 83.5037L123.1 94.3723L111.014 94.3488L111.035 83.4802ZM124.384 95.985L136.731 96.009L136.706 108.468L124.359 108.444L124.384 95.985ZM162.76 81.689L150.634 81.6654C150.634 81.6654 150.493 81.7255 150.413 81.7656L150.436 69.9511L162.783 69.9751L162.76 81.7092L162.76 81.689ZM136.447 55.5531L124.1 55.529L124.125 43.0704L136.472 43.0945L136.447 55.5531ZM84.8725 83.711L96.4152 83.7335L96.3945 94.3404L84.8519 94.3179L84.8725 83.711ZM149.851 122.28L137.765 122.257L137.791 109.315L149.876 109.339L149.851 122.28ZM189.445 81.7612L177.359 81.7376L177.382 70.0036L189.468 70.0271L189.445 81.7612ZM56.9489 69.2053C56.6472 69.2047 56.4054 69.4458 56.4048 69.7477L56.3784 83.2932C56.3778 83.5951 56.6187 83.8371 56.9203 83.8377L70.2326 83.8636L70.2101 95.4366C70.2095 95.7385 70.4503 95.9805 70.752 95.9811L83.0387 96.005L83.0146 108.383C83.014 108.685 83.2548 108.927 83.5565 108.928L96.1851 108.952L96.1593 122.196C96.1587 122.498 96.3995 122.74 96.7011 122.74L109.39 122.765L109.364 136.27C109.363 136.572 109.604 136.814 109.906 136.815L122.957 136.84L122.931 150.003C122.93 150.305 123.171 150.547 123.473 150.548L137.006 150.574C137.308 150.575 137.55 150.334 137.55 150.032L137.576 136.869L150.144 136.893C150.446 136.894 150.688 136.653 150.688 136.351L150.714 123.107L163.886 123.133C164.187 123.133 164.429 122.892 164.43 122.59L164.455 109.347L177.446 109.372C177.748 109.373 177.989 109.132 177.99 108.83L178.014 96.3511L190.784 96.376C191.085 96.3766 191.327 96.1356 191.328 95.8336L191.352 83.375L203.478 83.3987C203.78 83.3993 204.021 83.1582 204.022 82.8563L204.048 69.3108C204.049 69.0089 203.808 68.7669 203.506 68.7664L191.019 68.742L191.043 55.9413C191.044 55.6394 190.803 55.3974 190.502 55.3968L177.732 55.3719L177.757 42.8932C177.757 42.5912 177.516 42.3493 177.215 42.3487L164.224 42.3233L164.25 29.0798C164.25 28.7779 164.01 28.5359 163.708 28.5353L150.536 28.5096L150.562 15.2862C150.563 14.9843 150.322 14.7423 150.02 14.7417L137.452 14.7172L137.478 1.55418C137.478 1.25227 137.237 1.01028 136.936 1.00969L123.402 0.983322C123.101 0.982734 122.859 1.22378 122.858 1.52569L122.833 14.6887L109.782 14.6633C109.48 14.6627 109.238 14.9038 109.238 15.2057L109.211 28.7109L96.5225 28.6862C96.2208 28.6856 95.9791 28.9266 95.9785 29.2285L95.9527 42.4721L83.3241 42.4475C83.0224 42.4469 82.7806 42.6879 82.78 42.9899L82.7559 55.368L70.4692 55.344C70.1676 55.3434 69.9258 55.5845 69.9252 55.8864L69.8992 69.2306L56.9489 69.2053Z'
                fill='none'
              />
              <path
                d='M28.1894 46.8098L15.3798 46.7848L15.4033 34.769L28.2128 34.794L28.1894 46.8098ZM1.15895 48.6289L14.4713 48.6549L14.4472 61.0128C14.4466 61.3147 14.6875 61.5567 14.9891 61.5573L28.6634 61.584C28.965 61.5846 29.2068 61.3435 29.2074 61.0416L29.2334 47.6974L42.4049 47.7231C42.7066 47.7237 42.9483 47.4826 42.9489 47.1807L42.9756 33.4944C42.9762 33.1925 42.7354 32.9505 42.4337 32.9499L29.1214 32.9239L29.1455 20.566C29.1461 20.2641 28.9052 20.0221 28.6036 20.0215L14.9293 19.9948C14.6277 19.9942 14.3859 20.2353 14.3853 20.5372L14.3593 33.8814L1.18774 33.8557C0.886101 33.8551 0.644342 34.0962 0.643754 34.3981L0.617123 48.0643C0.616534 48.3662 0.857352 48.6082 1.15899 48.6088'
                fill='none'
              />
            </m.svg>
          </div>
        </div>
      </section>
    </>
  );
}
